import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { StandardButton, StandardDropdown, CARD_CLASS } from '../common/StandardUI';
import DashboardFilterModal, {
  EMPTY_FILTERS,
  countActiveFilters
} from '../B2BDashboard/DashboardFilterModal';
import Topbar from '../../container/PulseSurvey/Topbar';
import { CompanyApi } from '../../api/companyApi';
import { getLocalStorageItem, isCopanySuperOrSubAdmin, cn } from '../../utils/helper';
import DownloadReportButton from '../B2BDashboard/DownloadReportButton';
import { REPORT_SCOPE } from '../../api/companyReportApi';
import filterIcon from '../../assets/images/filter.svg';

/**
 * Filter state for a company section, so the pages inside a layout can read
 * what the admin picked without prop-drilling through the route wrapper.
 */
const CompanySectionFilterContext = createContext({ department: '', filters: EMPTY_FILTERS });

export const useCompanySectionFilter = () => useContext(CompanySectionFilterContext);

/**
 * Shell for the company panel's feature sections (Sleep, Habit Tracker,
 * Breathwork).
 *
 * Follows the Pulse Survey pattern: the section title sits on the background,
 * and one white card carries the current page and the controls that act on it.
 *
 * That card's heading tracks the dropdown rather than restating the section:
 * picking "Sounds Dashboard" retitles the card and swaps its description, so
 * the one line of prose always describes what is actually below it.
 *
 * `subtitle` is the fallback for a page that declares no description of its
 * own — it is never shown alongside one.
 *
 * `reportFeatureKey` opts the section into a PDF download. It belongs here
 * rather than in each layout because this component already owns the filter
 * state, so the report inherits exactly the population the section is showing
 * — an export that quietly covered the whole company while the screen showed
 * one department would be worse than no export.
 *
 * Only pass it once the feature has an adapter in the backend MANIFEST. A key
 * with no adapter renders a button that can only tell the admin the report
 * does not exist.
 */
const CompanySectionLayout = ({ title, subtitle, pages, reportFeatureKey = null, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState({
    countries: [],
    departments: [],
    ethnicities: [],
    loaded: false
  });

  useEffect(() => {
    if (!isCopanySuperOrSubAdmin()) {
      navigate('/login', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The department/country/ethnicity lists only populate the filter dialog, so
   * they are fetched the first time it opens rather than on mount — one fewer
   * request in the way of the section rendering.
   */
  useEffect(() => {
    if (!userData?.companyId || !isFilterOpen || filterData.loaded) return;
    CompanyApi.getFilterData(userData.companyId)
      .then((res) => {
        const data = res?.data?.data || res?.data || {};
        setFilterData({
          countries: Array.isArray(data.countries) ? data.countries : [],
          departments: Array.isArray(data.departments) ? data.departments : [],
          ethnicities: Array.isArray(data.ethnicities) ? data.ethnicities : [],
          loaded: true
        });
      })
      .catch(() => setFilterData((current) => ({ ...current, loaded: true })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.companyId, isFilterOpen, filterData.loaded]);

  // Longest matching path wins, so nested pages resolve to their own entry.
  const activePath = pages.reduce((best, page) => {
    const matches =
      location.pathname === page.path || location.pathname.startsWith(`${page.path}/`);
    if (!matches) return best;
    return !best || page.path.length > best.length ? page.path : best;
  }, '');

  const resolvedPath = activePath || pages[0]?.path;

  // The card header names the page you are on, not the section — the section
  // is already the <h1> above it, and repeating it wastes the only line that
  // could tell you what this page actually shows.
  const activePage = pages.find((page) => page.path === resolvedPath);

  const pageOptions = pages.map((page) => ({ label: page.label, value: page.path }));

  const activeFilterCount = countActiveFilters(filters);

  const filterValue = useMemo(
    () => ({
      // Lower-cased for the same reason the old department dropdown was: the
      // hub endpoints match on a normalised value. Kept as its own key so the
      // pages already reading `department` are unaffected.
      department: (filters.department || '').toLowerCase(),
      filters
    }),
    [filters]
  );

  return (
    <div className="relative lg:mb-[45px]">
      {/* Section title, breadcrumb, notification bell and profile menu — the
          same topbar every other screen carries. */}
      <Topbar title={title} />

      <div className={cn(CARD_CLASS, 'mt-4 p-5 sm:p-6')}>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
              {activePage?.label || title}
            </h2>
            {(activePage?.description || subtitle) && (
              <p className="text-sm text-shoorah-gray3 mt-1">
                {activePage?.description || subtitle}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <StandardDropdown
              label="Select page"
              options={pageOptions}
              value={resolvedPath}
              onChange={(path) => navigate(path)}
              align="right"
            />
            <StandardButton variant="secondary" onClick={() => setIsFilterOpen(true)}>
              <img src={filterIcon} alt="" className="w-4 h-4 dark:invert" />
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-shoorah-btn text-white dark:bg-shoorah-btnDark dark:text-shoorah-btn">
                  {activeFilterCount}
                </span>
              )}
            </StandardButton>
            {reportFeatureKey ? (
              <DownloadReportButton
                scope={REPORT_SCOPE.FEATURE}
                featureKey={reportFeatureKey}
                featureLabel={title}
                filters={filters}
              />
            ) : null}
          </div>
        </div>

        {/*
          The page itself lives inside this card, so the section reads as one
          surface with its own charts and tables nested in it — the Pulse
          Survey pattern — rather than a header floating above loose content.
        */}
        <div className="mt-5">
          <CompanySectionFilterContext.Provider value={filterValue}>
            {children}
          </CompanySectionFilterContext.Provider>
        </div>
      </div>

      <DashboardFilterModal
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onApply={setFilters}
        filterData={filterData}
      />
    </div>
  );
};

CompanySectionLayout.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      description: PropTypes.string
    })
  ).isRequired,
  reportFeatureKey: PropTypes.string,
  children: PropTypes.node
};

export default CompanySectionLayout;
